/**
 * Archetype identity (Wave C: KLA-040 emblem, KLA-041 quotes,
 * KLA-049 resemblance, KLA-047 share palette).
 *
 * Everything here is deterministic and grounded in the archetype the
 * person's own read produced. House rules: Klair voice, simple words,
 * zero em or en dashes, honest disclaimers, and famous names used only
 * as "shares a working trait", never a comparison of talent or a
 * prediction. No political figures.
 */

// Every archetype tag collapses into one of six working families.
const TAG_FAMILY = {
  enterprising: "enterprising",
  investigative: "investigative",
  social: "social",
  artistic: "artistic",
  realistic: "realistic",
  conventional: "conventional",
  conscientiousness: "conventional",
  achievement_value: "enterprising",
  mastery_motivator: "investigative",
  openness: "artistic",
  growth_motivator: "social",
  logical: "investigative",
  verbal: "social",
  extraversion: "social",
  agreeableness: "social",
  service_value: "social",
  creativity_value: "artistic",
};

export function familyOf(arch) {
  const tag = (arch?.tag || "").toLowerCase().split(/[ ,+]/)[0];
  return TAG_FAMILY[tag] || "investigative";
}

// One palette per family: [deep hue, bright hue, soft wash].
export const FAMILY_PALETTES = {
  enterprising: { deep: "#B3401A", bright: "#F97316", wash: "hsl(24 92% 95%)", hue: 24 },
  investigative: { deep: "#4432A8", bright: "#8B5CF6", wash: "hsl(255 50% 95%)", hue: 255 },
  social: { deep: "#0B7263", bright: "#14B8A6", wash: "hsl(172 55% 94%)", hue: 172 },
  artistic: { deep: "#9D1F63", bright: "#EC4899", wash: "hsl(330 70% 95%)", hue: 330 },
  realistic: { deep: "#8A5808", bright: "#F59E0B", wash: "hsl(38 90% 94%)", hue: 38 },
  conventional: { deep: "#1D4ED8", bright: "#60A5FA", wash: "hsl(219 70% 95%)", hue: 219 },
};

export function paletteFor(arch) {
  return FAMILY_PALETTES[familyOf(arch)] || FAMILY_PALETTES.investigative;
}

// The petal count makes each archetype's emblem its own: S7 gets seven
// petals, G3 gets thirteen (ten plus three) so Grow reads denser.
export function petalsFor(code) {
  const m = /^([SG])(\d+)$/.exec(code || "");
  if (!m) return 6;
  const n = parseInt(m[2], 10) || 6;
  return m[1] === "G" ? Math.min(n + 10, 16) : Math.max(n, 3);
}

// ---- KLA-041: the daily line, tuned to the archetype family --------

export const FAMILY_QUOTES = {
  enterprising: [
    "Momentum is built by the one who moves first. That is you today.",
    "Ask for the thing. The worst answer is a no you already have.",
    "Lead one small thing today, even if it is just the plan.",
    "Doors open for people already walking. Keep walking.",
    "Your energy is a resource. Spend it on what you want to grow.",
  ],
  investigative: [
    "Your questions are the sharpest tool you own. Ask one more.",
    "Understand it deeply once, and it stays yours forever.",
    "Slow thinking is not slow progress. It is how you win.",
    "One honest experiment beats ten opinions.",
    "The quiet work you do today will speak loudly later.",
  ],
  social: [
    "Someone around you is stuck. You are good at noticing. Notice.",
    "The message you are hesitating to send probably helps someone.",
    "People remember how you made them feel. You are rich in that.",
    "Helping one person think clearly is a real day's work.",
    "Your people skills are skills. Practice them on purpose.",
  ],
  artistic: [
    "Make the thing only you would make. That is the whole job.",
    "Imperfect and shared beats perfect and hidden.",
    "Your taste is ahead of your skill for now. Keep making, it catches up.",
    "The blank page is not judging you. Start anywhere.",
    "What you notice that others miss is your raw material.",
  ],
  realistic: [
    "One real thing finished today beats three planned for tomorrow.",
    "Your hands learn faster than your worries. Build something.",
    "Reliable is rare. Being the steady one is a superpower.",
    "Fix one small broken thing today. It compounds.",
    "Practical is not boring. Practical is what actually ships.",
  ],
  conventional: [
    "Order is a gift you give your future self. Set one thing straight.",
    "The details you catch are the ones that save the day later.",
    "A steady system beats a heroic sprint. You know this. Trust it.",
    "Precision is a form of care. Yours shows.",
    "Small, consistent, done right. That is how trust is built.",
  ],
};

// ---- KLA-049: famous-people trait resemblance ----------------------

export const RESEMBLANCE_DISCLAIMER =
  "This is a pattern echo, nothing more. You share a working trait " +
  "with these people, the way thousands of others do. It is not a " +
  "comparison of talent, not fame, and not a prediction.";

// Per archetype code: people widely admired for the SAME working
// pattern, with the one shared trait named plainly. Mix of Indian and
// global figures, no political figures.
export const RESEMBLANCE = {
  S1: [
    { name: "Virat Kohli", field: "cricket", trait: "leads from the front and makes the whole room raise its game" },
    { name: "Priyanka Chopra", field: "film and business", trait: "walks into new rooms and owns them" },
    { name: "Oprah Winfrey", field: "media", trait: "moves people by putting herself fully in it" },
  ],
  S2: [
    { name: "Srinivasa Ramanujan", field: "mathematics", trait: "quiet depth that saw what others could not" },
    { name: "Sundar Pichai", field: "technology", trait: "understated, sharp, three steps ahead" },
    { name: "Marie Curie", field: "science", trait: "let the work speak while she kept digging" },
  ],
  S3: [
    { name: "Sridhar Vembu", field: "software, Zoho", trait: "builds real things his own way, far from the noise" },
    { name: "Ritesh Agarwal", field: "business", trait: "turned a rough idea into a working thing, fast and young" },
    { name: "James Dyson", field: "engineering", trait: "kept making prototypes until the thing worked" },
  ],
  S4: [
    { name: "M.S. Dhoni", field: "cricket", trait: "calm under pressure, sorts the situation out" },
    { name: "Indra Nooyi", field: "business", trait: "practical, dependable, trusted with the hard calls" },
    { name: "Florence Nightingale", field: "healthcare", trait: "saw a broken system and quietly fixed it" },
  ],
  S5: [
    { name: "A.R. Rahman", field: "music", trait: "hears what is not there yet and makes it real" },
    { name: "S.S. Rajamouli", field: "film", trait: "imagines on a scale others call impossible" },
    { name: "Frida Kahlo", field: "art", trait: "turned her own truth into work nobody else could make" },
  ],
  S6: [
    { name: "Viswanathan Anand", field: "chess", trait: "sees the whole board and plays the long game" },
    { name: "Warren Buffett", field: "investing", trait: "patient strategy over quick wins" },
    { name: "Kiran Mazumdar-Shaw", field: "biotech", trait: "played a decades-long game others could not see" },
  ],
  S7: [
    { name: "Sudha Murty", field: "writing and philanthropy", trait: "connects people to exactly the help they need" },
    { name: "Harsha Bhogle", field: "broadcasting", trait: "brings people into the story so everyone belongs" },
    { name: "Trevor Noah", field: "comedy", trait: "bridges worlds that do not usually talk" },
  ],
  S8: [
    { name: "Rahul Dravid", field: "cricket", trait: "the wall: steady, precise, trusted absolutely" },
    { name: "Ratan Tata", field: "business", trait: "quiet dependability everything else gets built on" },
    { name: "Tim Cook", field: "technology", trait: "makes the complicated run on time, every time" },
  ],
  S9: [
    { name: "A.P.J. Abdul Kalam", field: "science and teaching", trait: "helped a generation think bigger, patiently" },
    { name: "Anand Kumar", field: "teaching, Super 30", trait: "guides people others gave up on" },
    { name: "Maria Montessori", field: "education", trait: "trusted the learner and built around them" },
  ],
  S10: [
    { name: "Kalpana Chawla", field: "spaceflight", trait: "followed curiosity all the way off the map" },
    { name: "David Attenborough", field: "natural history", trait: "a lifetime of wonder turned into work" },
    { name: "Ruskin Bond", field: "writing", trait: "wandered, noticed, and made it worth reading" },
  ],
  G1: [
    { name: "Ajay Banga", field: "business", trait: "runs the machine so the machine runs" },
    { name: "Tim Cook", field: "technology", trait: "operational calm at massive scale" },
  ],
  G2: [
    { name: "Taylor Swift", field: "music", trait: "ships finished work again and again, at pace" },
    { name: "Akshay Kumar", field: "film", trait: "output discipline most people cannot sustain" },
  ],
  G3: [
    { name: "Zakir Hussain", field: "music", trait: "a lifetime of care for details others miss" },
    { name: "Jiro Ono", field: "craft", trait: "the same work, refined forever" },
  ],
  G4: [
    { name: "Pullela Gopichand", field: "coaching", trait: "builds champions instead of being the star" },
    { name: "Phil Jackson", field: "coaching", trait: "grows the people around him first" },
  ],
  G5: [
    { name: "C.R. Rao", field: "statistics", trait: "turned tables of numbers into lasting decisions" },
    { name: "Abhijit Banerjee", field: "economics", trait: "tests what everyone assumes" },
  ],
  G6: [
    { name: "Harsha Bhogle", field: "broadcasting", trait: "makes a hard idea land in every room" },
    { name: "Simon Sinek", field: "speaking", trait: "gives a fuzzy feeling a clear sentence" },
  ],
  G7: [
    { name: "Nandan Nilekani", field: "technology", trait: "sees the loops in a system and tightens them" },
    { name: "W. Edwards Deming", field: "quality", trait: "fixed systems, not blame" },
  ],
  G8: [
    { name: "Satya Nadella", field: "technology", trait: "moved a giant from A to B without breaking it" },
    { name: "Verghese Kurien", field: "cooperatives", trait: "changed how a whole country worked, patiently" },
  ],
  G9: [
    { name: "Azim Premji", field: "business and giving", trait: "trust held across decades" },
    { name: "Charlie Munger", field: "investing", trait: "the partner who makes the pair stronger" },
  ],
  G10: [
    { name: "JRD Tata", field: "industry", trait: "started new things and kept them alive" },
    { name: "Falguni Nayar", field: "business, Nykaa", trait: "founded late, proved it is never too late" },
    { name: "Sara Blakely", field: "business", trait: "built from zero on stubborn belief" },
  ],
};

export function resemblanceFor(arch) {
  const code = (arch?.code || "").toUpperCase();
  return RESEMBLANCE[code] || null;
}
